import { useState } from "react";
import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api";
import { Icon } from "../lib/Icon";
import { NAV, NAV_GROUPS } from "./nav";
import { useScope } from "./scope";
import { WorkTimer } from "./WorkTimer";
import { Softphone } from "./Softphone";

export function Shell() {
  const [collapsed, setCollapsed] = useState(false);
  const { client, setClient } = useScope();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { data: clients } = useQuery({ queryKey: ["clients"], queryFn: api.clients });
  // Docked softphone shows on every screen only when the telephony flag is on
  // (off by default → renders nothing).
  const { data: flags } = useQuery({ queryKey: ["flags"], queryFn: api.flags });

  const scopeLabel = client ? clients?.find((c) => c.id === client)?.name ?? client : "All clients";

  function cycleScope() {
    if (!clients || clients.length === 0) return;
    if (client === null) return setClient(clients[0]!.id);
    const idx = clients.findIndex((c) => c.id === client);
    if (idx === clients.length - 1) return setClient(null);
    setClient(clients[idx + 1]!.id);
  }

  const navExpanded = !collapsed;

  return (
    <div
      style={{
        height: "100vh",
        display: "grid",
        gridTemplateColumns: `${collapsed ? 60 : 232}px minmax(0,1fr)`,
        gridTemplateRows: "60px minmax(0,1fr)",
        overflow: "hidden",
        background: "#f4f3ef",
        transition: "grid-template-columns .15s",
      }}
    >
      {/* HEADER */}
      <header
        style={{
          gridColumn: "1/3",
          background: "#006747",
          display: "flex",
          alignItems: "center",
          padding: "0 20px",
          gap: 16,
          boxShadow: "0 1px 0 rgba(0,0,0,.12)",
          zIndex: 6,
        }}
      >
        <button
          onClick={() => setCollapsed((v) => !v)}
          title="Collapse navigation"
          style={{ border: 0, background: "rgba(255,255,255,.12)", borderRadius: 6, width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flex: "none" }}
        >
          <Icon name={collapsed ? "menu" : "panel-left-close"} size={16} color="#fff" />
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 11, flex: "none" }}>
          <svg width="30" height="30" viewBox="0 0 100 100" style={{ flex: "none" }}>
            <rect x="13" y="13" width="74" height="74" rx="2" fill="none" stroke="#fff" strokeWidth="6" />
            <g transform="translate(50,50) scale(0.78) translate(-50,-52)">
              <path d="M18 80 A8 8 0 0 1 34 80 A8 8 0 0 1 50 80 A8 8 0 0 1 66 80 A8 8 0 0 1 82 80 M26 64 A8 8 0 0 1 42 64 A8 8 0 0 1 58 64 A8 8 0 0 1 74 64 M34 48 A8 8 0 0 1 50 48 A8 8 0 0 1 66 48 M42 32 A8 8 0 0 1 58 32" fill="none" stroke="#fff" strokeWidth="7" strokeLinecap="round" />
            </g>
          </svg>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <span style={{ fontWeight: 600, fontSize: 19, letterSpacing: "-0.6px", color: "#fff" }}>Recursive</span>
            <span style={{ fontWeight: 500, fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "rgba(255,255,255,.6)" }}>Staff</span>
          </div>
        </div>
        <div style={{ flex: "1 1 120px", minWidth: 0, maxWidth: 460, display: "flex", alignItems: "center", gap: 9, background: "rgba(255,255,255,.12)", border: "1px solid rgba(255,255,255,.16)", borderRadius: 7, padding: "8px 12px" }}>
          <Icon name="search" size={15} color="rgba(255,255,255,.8)" />
          <input placeholder="Search tickets, members, tenants, TRI keys…" style={{ flex: 1, border: 0, background: "transparent", outline: 0, fontSize: 13, color: "#fff", minWidth: 0 }} />
          <span style={{ fontSize: 11, color: "rgba(255,255,255,.45)", border: "1px solid rgba(255,255,255,.2)", borderRadius: 4, padding: "1px 6px" }}>⌘K</span>
        </div>
        <div style={{ flex: 1 }} />
        <WorkTimer />
        <button
          onClick={cycleScope}
          title="Tenant scope"
          style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,.12)", border: "1px solid rgba(255,255,255,.16)", borderRadius: 7, padding: "7px 11px", cursor: "pointer", color: "#fff" }}
        >
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#7fe3b0" }} />
          <span style={{ fontSize: 12, fontWeight: 500, whiteSpace: "nowrap" }}>{scopeLabel}</span>
          <Icon name="chevron-down" size={14} color="rgba(255,255,255,.8)" />
        </button>
        {/* Call/Text open the docked softphone on the matching tab via a window
            event (see Softphone). Only shown when telephony is on — the softphone
            they'd open renders under the same flag, so no dead buttons. */}
        {flags?.telephony && (
          <div style={{ display: "flex", alignItems: "center", flex: "none", background: "rgba(255,255,255,.12)", border: "1px solid rgba(255,255,255,.16)", borderRadius: 7, overflow: "hidden" }}>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent("softphone:open", { detail: { tab: "dial" } }))}
              style={{ display: "flex", alignItems: "center", gap: 6, border: 0, background: "transparent", cursor: "pointer", padding: "7px 10px", color: "#fff", fontSize: 12, fontWeight: 600 }}
            >
              <Icon name="phone-outgoing" size={15} color="#fff" />
              Call
            </button>
            <span style={{ width: 1, height: 18, background: "rgba(255,255,255,.2)" }} />
            <button
              onClick={() => window.dispatchEvent(new CustomEvent("softphone:open", { detail: { tab: "text" } }))}
              style={{ display: "flex", alignItems: "center", gap: 6, border: 0, background: "transparent", cursor: "pointer", padding: "7px 10px", color: "#fff", fontSize: 12, fontWeight: 600 }}
            >
              <Icon name="message-square" size={15} color="#fff" />
              Text
            </button>
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 9, flex: "none" }}>
          <div style={{ width: 31, height: 31, borderRadius: "50%", background: "#e7e1d4", color: "#006747", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600, fontSize: 12.5 }}>DL</div>
          <div style={{ lineHeight: 1.15 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: "#fff" }}>Devon Lang</div>
            <div style={{ fontSize: 10.5, color: "rgba(255,255,255,.6)" }}>Support · TRIBUS</div>
          </div>
        </div>
      </header>

      {/* SIDEBAR */}
      <nav style={{ background: "#fff", borderRight: "1px solid #ececec", padding: "14px 10px", display: "flex", flexDirection: "column", overflowY: "auto", overflowX: "hidden", gap: 2 }}>
        {NAV_GROUPS.map((group) => (
          <div key={group}>
            {navExpanded ? (
              <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase", color: "#a8aca6", padding: "16px 10px 7px", whiteSpace: "nowrap", overflow: "hidden" }}>{group}</div>
            ) : (
              <div style={{ height: 12 }} />
            )}
            {NAV.filter((n) => n.group === group).map((n) => {
              // The ticket detail lives at /tickets/<id> but belongs to the Inbox nav.
              const active =
                n.path === "/"
                  ? pathname === "/"
                  : pathname.startsWith(n.path) || (n.path === "/inbox" && pathname.startsWith("/tickets/"));
              return (
                <Link
                  key={n.id}
                  to={n.path}
                  title={n.label}
                  style={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    gap: 11,
                    padding: "9px 11px",
                    borderRadius: 7,
                    cursor: "pointer",
                    background: active ? "#eef4f1" : "transparent",
                    color: active ? "#006747" : "#4a504a",
                    fontWeight: active ? 600 : 500,
                    fontSize: 14,
                    whiteSpace: "nowrap",
                    textDecoration: "none",
                  }}
                >
                  <span style={{ position: "absolute", left: -10, top: 8, bottom: 8, width: 3, borderRadius: 3, background: "#006747", opacity: active ? 1 : 0 }} />
                  <Icon name={n.icon} size={18} color={active ? "#006747" : "#8a8e88"} />
                  {navExpanded && <span style={{ flex: 1 }}>{n.label}</span>}
                </Link>
              );
            })}
          </div>
        ))}
        <div style={{ flex: 1 }} />
        <div style={{ borderTop: "1px solid #ececec", margin: "8px 4px 0", paddingTop: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 11, padding: "9px 12px", borderRadius: 7, color: "#4a504a", fontSize: 14, cursor: "pointer" }}>
            <Icon name="settings" size={18} color="#8a8e88" />
            {navExpanded && <span>Settings</span>}
          </div>
        </div>
      </nav>

      {/* MAIN */}
      <main style={{ minHeight: 0, minWidth: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <Outlet />
      </main>

      {flags?.telephony && <Softphone />}
    </div>
  );
}
